import {
	clearManagedAccountAccessSession,
	getManagedAccountAccessSession,
	setManagedAccountAccessSession,
} from '../../../models/ManagedAccountAccessSession';
import { UserSessionContext } from '../../../viewmodels/AppViewModels';
import { BrowserPushNotificationsViewModel } from '../../../viewmodels/PushNotifications';

/**
 * Ends the session on the server before local state is torn down, so a failure is visible rather than
 * leaving a session the server still honours behind a cleared cookie. The push subscription is removed
 * first, while the session can still authorise that call. A bearer session has no server session to end.
 *
 * A stored token (managed-account access, or a leftover bearer sign-in) is set aside for the call: a request
 * that carries one is sent without the cookie, so the cookie would survive. It is put back if the call fails,
 * so the session the user is still looking at keeps running as the account it shows.
 *
 * Deliberately not in `models/utils/uiUtils`: that module is imported during `AppState` initialisation,
 * and pulling the push view model in there inverts the module order and leaves the app unable to boot.
 */
export async function logoutFromServer(userSession?: UserSessionContext) {
	if (!userSession?.webServiceHelper?.cookieSession) {
		return { success: true };
	}

	const userId = userSession.user?.id;
	if (userId) {
		try {
			await BrowserPushNotificationsViewModel.removeSubscription(userSession.webServiceHelper, userId);
		} catch (error) {
			// A stale push subscription is recoverable; a server session left running is not.
			console.error('Failed to remove push subscription during logout:', error);
		}
	}

	const credentialStore = userSession.webServiceHelper.getCredentialStore();
	const storedCredential = await credentialStore.getCredential();
	const managedSession = await getManagedAccountAccessSession();
	await credentialStore.removeCredential?.();
	clearManagedAccountAccessSession();

	const result = await userSession.webServiceHelper.callWebServiceAsync('user/logout', 'POST');
	// A 401 means the server session had already ended, which is the outcome logout is after.
	if (result.success || result.systemCode === 401) {
		return { success: true };
	}

	if (storedCredential?.access_token) {
		await credentialStore.setCredential(storedCredential);
	}
	if (managedSession) {
		await setManagedAccountAccessSession(managedSession);
	}
	return result;
}
